import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { Button, ButtonBase, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CheckIcon from '@mui/icons-material/Check';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TabHeader from '../components/TabHeader';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import Spinner from '../components/Spinner';
import FaqItemAccordion from '../features/faq/components/FaqItemAccordion';
import FaqModal from '../features/faq/components/FaqModal';
import FaqCategoryFilter from '../features/faq/components/FaqCategoryFilter';
import { useFaqItems } from '../features/faq/hooks/useFaqItems';
import { FAQ_CATEGORIES } from '../features/faq/constants';
import type { FaqCategoryKey, FaqItem } from '../features/faq/types';
import { useNotification } from '../context/useNotification';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { APP_LABELS, FAQ_PAGE_LABELS as LABELS } from '../utils/labels';

interface HelpPageProps {
  isSuperAdmin: boolean;
}

interface FaqModalState {
  mode: 'create' | 'edit';
  item?: FaqItem;
}

interface FaqReorderRowProps {
  item: FaqItem;
}

function FaqReorderRow({ item }: FaqReorderRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 border border-border rounded-xl mb-2 px-3 py-2.5 bg-surface ${isDragging ? 'opacity-50' : ''}`}
      data-testid={`faq-reorder-row-${item.id}`}
    >
      <Tooltip title={LABELS.dragHandle}>
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing flex items-center text-text-secondary"
          aria-label={LABELS.dragHandle}
          data-testid="drag-handle"
        >
          <DragIndicatorIcon fontSize="small" />
        </span>
      </Tooltip>
      <p className="mb-0 mt-0 flex-1 truncate">{item.question}</p>
    </div>
  );
}

export default function HelpPage({ isSuperAdmin }: HelpPageProps) {
  const navigate = useNavigate();
  const { faqItems, loading, error, addFaqItem, updateFaqItem, deleteFaqItem, reorderFaqItems } = useFaqItems();
  const { notifySuccess, notifyError } = useNotification();
  useDocumentTitle(`${LABELS.title} · ${APP_LABELS.appName}`);
  const sensors = useSensors(useSensor(PointerSensor));

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<FaqCategoryKey>(FAQ_CATEGORIES[0].key);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [faqModal, setFaqModal] = useState<FaqModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const categoryLabel = FAQ_CATEGORIES.find((c) => c.key === category)?.label ?? '';

  const categoryFiltered = faqItems
    .filter((f) => f.category_key === category)
    .sort((a, b) => a.sort_order - b.sort_order);

  const fuse = useMemo(
    () =>
      new Fuse(faqItems, {
        keys: [{ name: 'question', weight: 2 }, 'answer'],
        threshold: 0.35,
        minMatchCharLength: 1,
        ignoreLocation: true,
      }),
    [faqItems],
  );

  const query = search.trim();
  const isSearching = query.length > 0;
  const searchResults = isSearching ? fuse.search(query).map((r) => r.item) : [];

  const handleToggleReorderMode = () => {
    setSearch('');
    setIsReorderMode((prev) => !prev);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = categoryFiltered.map((f) => f.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    reorderFaqItems(arrayMove(ids, oldIndex, newIndex)).then((ok) => {
      if (ok) notifySuccess(LABELS.reorderSuccess); else notifyError(LABELS.reorderError);
    });
  };

  const handleSave = async (categoryKey: FaqCategoryKey, question: string, answer: string): Promise<boolean> => {
    const ok =
      faqModal?.mode === 'create'
        ? await addFaqItem(categoryKey, question, answer)
        : faqModal?.item
          ? await updateFaqItem(faqModal.item.id, categoryKey, question, answer)
          : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    const ok = await deleteFaqItem(id);
    if (ok) notifySuccess(LABELS.deleteSuccess); else notifyError(LABELS.deleteError);
  };

  return (
    <div className="h-dvh flex flex-col bg-background" data-testid="help-page">
      <header className="shrink-0 bg-surface border-b border-border px-4 py-2">
        <ButtonBase onClick={() => navigate('/')} aria-label={APP_LABELS.appName} className="rounded">
          <img src="/planly-logo-cropped.svg" alt={APP_LABELS.appName} className="h-9 w-auto md:hidden" />
          <img src="/planly-logo-light.svg" alt={APP_LABELS.appName} className="h-9 w-auto hidden md:block dark:hidden" />
          <img src="/planly-logo-dark.svg" alt={APP_LABELS.appName} className="h-9 w-auto hidden md:dark:block" />
        </ButtonBase>
      </header>
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-4">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={isSuperAdmin && !isReorderMode ? LABELS.addLabel : undefined}
            onAdd={isSuperAdmin && !isReorderMode ? () => setFaqModal({ mode: 'create' }) : undefined}
          />

          {!isReorderMode && (
            <TextField
              placeholder={LABELS.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              slotProps={{
                htmlInput: { 'aria-label': LABELS.searchLabel },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" className="text-text-secondary" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <Tooltip title={LABELS.clearSearch}>
                        <IconButton
                          onClick={() => setSearch('')}
                          aria-label={LABELS.clearSearch}
                          size="small"
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2">
              <p className="text-sm text-red-700 mb-0 mt-0">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-4">
              {!isSearching && <FaqCategoryFilter value={category} onChange={setCategory} />}
              <div className="flex-1 min-w-0 space-y-4">
                {isSearching ? (
                  searchResults.length === 0 ? (
                    <p className="text-sm text-text-secondary text-center py-6">{LABELS.noResults}</p>
                  ) : (
                    <section>
                      {searchResults.map((item) => (
                        <FaqItemAccordion
                          key={item.id}
                          item={item}
                          defaultExpanded
                          onEdit={isSuperAdmin ? () => setFaqModal({ mode: 'edit', item }) : undefined}
                          onDelete={isSuperAdmin ? () => setPendingDeleteId(item.id) : undefined}
                        />
                      ))}
                    </section>
                  )
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold mb-0 mt-0 flex-1">{categoryLabel}</h2>
                      {isSuperAdmin && (
                        <Button
                          size="small"
                          variant={isReorderMode ? 'contained' : 'outlined'}
                          color={isReorderMode ? 'success' : 'inherit'}
                          startIcon={isReorderMode ? <CheckIcon /> : <SwapVertIcon />}
                          onClick={handleToggleReorderMode}
                          data-testid="reorder-toggle-button"
                        >
                          {isReorderMode ? LABELS.reorderModeExit : LABELS.reorderMode}
                        </Button>
                      )}
                    </div>

                    {isReorderMode ? (
                      categoryFiltered.length === 0 ? (
                        <p className="text-sm text-text-secondary text-center py-6">{LABELS.noResults}</p>
                      ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                          <SortableContext
                            items={categoryFiltered.map((f) => f.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {categoryFiltered.map((item) => (
                              <FaqReorderRow key={item.id} item={item} />
                            ))}
                          </SortableContext>
                        </DndContext>
                      )
                    ) : categoryFiltered.length === 0 ? (
                      <p className="text-sm text-text-secondary text-center py-6">{LABELS.noResults}</p>
                    ) : (
                      <section>
                        {categoryFiltered.map((item) => (
                          <FaqItemAccordion
                            key={item.id}
                            item={item}
                            onEdit={isSuperAdmin ? () => setFaqModal({ mode: 'edit', item }) : undefined}
                            onDelete={isSuperAdmin ? () => setPendingDeleteId(item.id) : undefined}
                          />
                        ))}
                      </section>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {faqModal && (
        <FaqModal item={faqModal.item} onClose={() => setFaqModal(null)} onSave={handleSave} />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
